import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Dialog,
  ErrorState,
  Field,
  Input,
  Select,
  useToast,
} from '@/components/ui';
import { parseRM, senToInput } from '@/features/billing/money';
import { isApiError } from '@/lib/api';
import {
  useApplyLimitOverride,
  type LimitBillingAssessment,
  type LimitOverrideKind,
} from './api';
import styles from './LimitBilling.module.css';

export function PanelApprovalOverrideControl({
  assessment,
  onAssessmentStale,
}: {
  assessment: LimitBillingAssessment;
  onAssessmentStale: (error: unknown) => void;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const apply = useApplyLimitOverride();
  const [open, setOpen] = useState(false);
  const [kind, setKind] = useState<LimitOverrideKind>('panel_approval');
  const [reference, setReference] = useState('');
  const [reason, setReason] = useState('');
  const [coverage, setCoverage] = useState(
    senToInput(assessment.split.eligibleTotalSen),
  );
  const [attempted, setAttempted] = useState(false);
  const coverageSen = parseRM(coverage);
  const invalid =
    !reference.trim() ||
    !reason.trim() ||
    coverageSen === null ||
    coverageSen > assessment.split.billTotalSen;
  const apiError = isApiError(apply.error) ? apply.error : undefined;

  const submit = (event: FormEvent) => {
    event.preventDefault();
    setAttempted(true);
    if (invalid || coverageSen === null) return;
    apply.mutate(
      {
        billId: assessment.billId,
        assessmentId: assessment.id,
        kind,
        reference: reference.trim(),
        reason: reason.trim(),
        approvedCoverageSen: coverageSen,
      },
      {
        onSuccess: () => {
          toast.show({
            tone: 'success',
            title: t('limitBilling.overrideApplied'),
          });
          setAttempted(false);
          setOpen(false);
        },
        onError: onAssessmentStale,
      },
    );
  };

  return (
    <>
      <Button onClick={() => setOpen(true)}>
        {t('limitBilling.enterPanelApproval')}
      </Button>
      <Dialog
        open={open}
        title={t('limitBilling.enterPanelApproval')}
        onClose={() => setOpen(false)}
        footer={
          <>
            <Button onClick={() => setOpen(false)} disabled={apply.isPending}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="primary"
              type="submit"
              form="panel-approval-form"
              loading={apply.isPending}
            >
              {t('limitBilling.applyOverride')}
            </Button>
          </>
        }
      >
        <form
          id="panel-approval-form"
          className={styles.form}
          onSubmit={submit}
        >
          <p className={styles.note}>{t('limitBilling.overrideBody')}</p>
          <Field label={t('limitBilling.overrideType')}>
            <Select
              value={kind}
              onChange={(event) =>
                setKind(event.target.value as LimitOverrideKind)
              }
              options={[
                {
                  value: 'panel_approval',
                  label: t('limitBilling.panelApproval'),
                },
                {
                  value: 'guarantee_letter',
                  label: t('limitBilling.guaranteeLetter'),
                },
              ]}
            />
          </Field>
          <Field
            label={t('limitBilling.reference')}
            error={
              attempted && !reference.trim()
                ? t('limitBilling.referenceRequired')
                : undefined
            }
          >
            <Input
              value={reference}
              onChange={(event) => setReference(event.target.value)}
            />
          </Field>
          <Field
            label={t('limitBilling.approvedCoverage')}
            error={
              attempted &&
              (coverageSen === null ||
                coverageSen > assessment.split.billTotalSen)
                ? t('limitBilling.coverageInvalid')
                : undefined
            }
          >
            <Input
              inputMode="decimal"
              value={coverage}
              onChange={(event) => setCoverage(event.target.value)}
            />
          </Field>
          <Field
            label={t('limitBilling.reason')}
            hint={t('limitBilling.reasonHint')}
            error={
              attempted && !reason.trim()
                ? t('limitBilling.reasonRequired')
                : undefined
            }
          >
            <textarea
              rows={3}
              value={reason}
              onChange={(event) => setReason(event.target.value)}
            />
          </Field>
          {apiError && <ErrorState error={apiError} />}
        </form>
      </Dialog>
    </>
  );
}
